/**
 * A paper as its outline: the only view of a paper that helps you place
 * something in it (Olivia, tables-modern 1b).
 *
 * Sections come from the headings. Numbers come from the paper's last compile
 * (`LabelNumbers`, served by `GET /api/documents/{id}/references`): a table's
 * number is known once the paper has been compiled, and until then the section
 * stands alone (ANSWERS-from-lilia §2). Pure, so the rules are tested here.
 */

export interface PaperBlock {
  id: string;
  type: string;
  content: Record<string, unknown>;
  sortOrder: number;
}

export interface PaperHeading {
  id: string;
  /** "4", "4.3", or "" for an unnumbered heading. */
  number: string;
  title: string;
  level: number;
}

export interface PaperSection extends PaperHeading {
  /** The table and figure blocks inside the section, in order. */
  tables: string[];
  figures: string[];
}

/** Where a table can land: the end of a section, or of the document. */
export interface PaperSlot {
  /** Section index it follows, or -1 for "the end of the document" in a paper with no sections. */
  after: number;
  /** The sortOrder the new block takes (later blocks shift down); null appends. */
  sortOrder: number | null;
  /** How many table blocks come before it. */
  tablesBefore: number;
}

export interface PaperOutline {
  sections: PaperSection[];
  slots: PaperSlot[];
  headings: PaperHeading[];
  tableCount: number;
  figureCount: number;
}

const headingText = (b: PaperBlock) => String(b.content.text ?? "").trim() || "Untitled section";
const headingLevel = (b: PaperBlock) => Number(b.content.level) || 1;
const isNumbered = (b: PaperBlock) => b.content.numbered !== false;

/** Blocks that sit after the last section's prose and stay last. */
const TRAILING = new Set(["bibliography"]);

export function paperOutline(input: PaperBlock[]): PaperOutline {
  const blocks = [...input].sort((a, b) => a.sortOrder - b.sortOrder);
  const heads = blocks.filter((b) => b.type === "heading");
  const top = heads.length ? Math.min(...heads.map(headingLevel)) : 1;

  // Hierarchical numbers, the way LaTeX counts: a section resets its subsections.
  const counters: number[] = [];
  const headings: PaperHeading[] = heads.map((b) => {
    const depth = Math.max(0, headingLevel(b) - top);
    let number = "";
    if (isNumbered(b)) {
      counters[depth] = (counters[depth] ?? 0) + 1;
      counters.length = depth + 1;
      number = counters.map((n) => n ?? 0).join(".");
    }
    return { id: b.id, number, title: headingText(b), level: headingLevel(b) };
  });

  const sections: PaperSection[] = [];
  const starts: number[] = [];
  blocks.forEach((b, i) => {
    if (b.type === "heading" && headingLevel(b) === top) {
      const h = headings.find((x) => x.id === b.id)!;
      sections.push({ ...h, tables: [], figures: [] });
      starts.push(i);
    } else if (sections.length) {
      const s = sections[sections.length - 1];
      if (b.type === "table") s.tables.push(b.id);
      if (b.type === "figure") s.figures.push(b.id);
    }
  });

  const trailing = blocks.findIndex((b, i) => TRAILING.has(b.type) && blocks.slice(i).every((x) => TRAILING.has(x.type)));
  const tailSort = trailing >= 0 ? blocks[trailing].sortOrder : null;
  const tablesBeforeIndex = (i: number) => blocks.slice(0, i).filter((b) => b.type === "table").length;
  const tableCount = blocks.filter((b) => b.type === "table").length;

  const slots: PaperSlot[] = sections.length
    ? sections.map((_, s) => {
        const next = starts[s + 1];
        if (next !== undefined) {
          return { after: s, sortOrder: blocks[next].sortOrder, tablesBefore: tablesBeforeIndex(next) };
        }
        const end = trailing >= 0 ? trailing : blocks.length;
        return { after: s, sortOrder: tailSort, tablesBefore: tablesBeforeIndex(end) };
      })
    : [{ after: -1, sortOrder: tailSort, tablesBefore: tablesBeforeIndex(trailing >= 0 ? trailing : blocks.length) }];

  return {
    sections,
    slots,
    headings,
    tableCount,
    figureCount: blocks.filter((b) => b.type === "figure").length,
  };
}

/**
 * Where a table lands by default: after the last section that already holds a
 * table, otherwise at the end of the document.
 */
export function defaultSlot(o: PaperOutline): number {
  for (let s = o.sections.length - 1; s >= 0; s--) {
    if (o.sections[s].tables.length) return s;
  }
  return o.slots.length - 1;
}

/** The heading a block sits under, or null before the first heading. */
export function sectionOf(input: PaperBlock[], blockId: string, o: PaperOutline = paperOutline(input)): PaperHeading | null {
  const blocks = [...input].sort((a, b) => a.sortOrder - b.sortOrder);
  let current: PaperHeading | null = null;
  for (const b of blocks) {
    if (b.type === "heading") current = o.headings.find((h) => h.id === b.id) ?? current;
    if (b.id === blockId) return current;
  }
  return null;
}

/** "§4 Results", or "Results" for an unnumbered heading. */
export function sectionLabel(h: Pick<PaperHeading, "number" | "title">): string {
  return h.number ? `§${h.number} ${h.title}` : h.title;
}

/**
 * The number a table landing in `slot` would print, from the last compile's
 * numbers: one after the tables before it. Null when the paper has not been
 * compiled, or numbers its tables by chapter (4.2), where counting would lie.
 */
export function predictTableNumber(
  slot: PaperSlot,
  numbered: boolean,
  knownNumbers: (string | null | undefined)[],
): string | null {
  if (!numbered) return null;
  if (knownNumbers.some((n) => n && !/^\d+$/.test(n))) return null;
  return String(slot.tablesBefore + 1);
}

/** "§4 Results · Table 3", or the section alone before a compile. */
export function placeLabel(section: PaperHeading | null, number: string | null | undefined): string {
  const parts: string[] = [];
  if (section) parts.push(sectionLabel(section));
  if (number) parts.push(`Table ${number}`);
  return parts.join(" · ");
}
